import { useCallback, useEffect, useRef, useState } from 'react';
import axios, { CancelToken } from 'axios';

export interface LookupOptions<T> {
  options: T[];
  hasMore?: boolean;
}

interface Props<T> {
  fetchOptions: (query: string, cancelToken: CancelToken) => Promise<LookupOptions<T>>;
  enabled: boolean;
  suggestionsEnabled: boolean;
  fetchMinimumInputLength: number;
  debounceMs?: number;
}

export function useLookupOptions<T>({ fetchOptions, enabled, suggestionsEnabled, fetchMinimumInputLength, debounceMs = 400 }: Props<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [attempt, setAttempt] = useState(0);
  const [data, setData] = useState<LookupOptions<T>>({ options: [] });
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const suggestions = useRef<{ fetchOptions: Props<T>['fetchOptions']; data: LookupOptions<T> }>();
  const query = searchValue.trim();

  useEffect(() => {
    setData({ options: [] });
    setHasError(false);
    if (!enabled || !isOpen || (!query && !suggestionsEnabled) || (query.length > 0 && query.length < fetchMinimumInputLength)) {
      setIsLoading(false);
      return undefined;
    }

    if (!query && suggestions.current?.fetchOptions === fetchOptions) {
      setData(suggestions.current.data);
      setIsLoading(false);
      return undefined;
    }

    const source = axios.CancelToken.source();
    let current = true;
    setIsLoading(true);
    const timer = setTimeout(
      async () => {
        try {
          const result = await fetchOptions(query, source.token);
          if (current) {
            setData(result);
            if (!query) suggestions.current = { fetchOptions, data: result };
          }
        } catch (error) {
          if (current && !axios.isCancel(error)) setHasError(true);
        } finally {
          if (current) setIsLoading(false);
        }
      },
      query ? debounceMs : 0
    );

    return () => {
      current = false;
      clearTimeout(timer);
      source.cancel();
    };
  }, [fetchOptions, enabled, isOpen, query, suggestionsEnabled, fetchMinimumInputLength, debounceMs, attempt]);

  const handleOpen = useCallback(function handleOpen() {
    setIsOpen(true);
  }, []);

  const handleClose = useCallback(function handleClose() {
    setIsOpen(false);
    setSearchValue('');
  }, []);

  const handleSearch = useCallback(function handleSearch(value: string) {
    setSearchValue(value);
  }, []);

  function handleRetry() {
    suggestions.current = undefined;
    setAttempt(value => value + 1);
  }

  return { ...data, isLoading, hasError, searchValue: query, handleOpen, handleClose, handleSearch, handleRetry };
}
